import React, { useState, useEffect } from 'react';

function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

const PAGE_SIZE = 10;

const EMPTY_FORM = {
  title: '',
  publicLaw: '',
  effectiveDate: '',
  usCitation: '',
  cfrCitation: '',
  sourceReference: '',
  summary: '',
};

const PencilIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const LawsPage = () => {
  const [laws, setLaws] = useState([]);
  const [fetchError, setFetchError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [page, setPage] = useState(1);

  const [showForm, setShowForm] = useState(false);
  const [editingLawId, setEditingLawId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const token = localStorage.getItem('token');
  const payload = token ? decodeToken(token) : null;
  const roles = payload
    ? Array.isArray(payload.roles) ? payload.roles : [payload.role].filter(Boolean)
    : [];
  const canManage = roles.includes('Super Admin') || roles.includes('Grant Manager');

  useEffect(() => {
    const fetchLaws = async () => {
      try {
        const res = await fetch('/api/laws', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load laws');
        setLaws(await res.json());
      } catch {
        setFetchError('Could not load laws.');
      }
    };
    if (token) fetchLaws();
  }, [token]);

  const filtered = laws.filter((l) =>
    l.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const firstIdx = (safePage - 1) * PAGE_SIZE;
  const lastIdx = Math.min(safePage * PAGE_SIZE, filtered.length);
  const paged = filtered.slice(firstIdx, lastIdx);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleToggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allPageSelected = paged.length > 0 && paged.every((l) => selectedIds.has(l.id));

  const handleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paged.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paged.forEach((l) => next.add(l.id));
        return next;
      });
    }
  };

  const handleRemoveSelected = () => {
    const idsToRemove = [...selectedIds];
    if (idsToRemove.length === 0) return;

    const label = idsToRemove.length === 1 ? 'this law' : `${idsToRemove.length} selected laws`;
    if (!window.confirm(`Remove ${label}?`)) return;

    Promise.all(
      idsToRemove.map((id) =>
        fetch(`/api/laws/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        })
      )
    )
      .then(async (responses) => {
        const failedResponse = responses.find((response) => !response.ok);
        if (failedResponse) {
          let message = 'Failed to remove the selected laws.';
          try {
            const body = await failedResponse.json();
            message = body.message || message;
          } catch { }
          throw new Error(message);
        }

        setLaws((prev) => prev.filter((law) => !selectedIds.has(law.id)));
        setExpandedIds((prev) => {
          const next = new Set(prev);
          idsToRemove.forEach((id) => next.delete(id));
          return next;
        });
        setSelectedIds(new Set());
        showSuccess(idsToRemove.length === 1 ? 'Law removed successfully' : 'Selected laws removed successfully');
      })
      .catch((removeError) => {
        setSubmitError(removeError.message || 'Could not remove the selected laws.');
      });
  };

  const handleDeleteLaw = async (law) => {
    if (!window.confirm(`Remove "${law.title}"?`)) return;

    try {
      setSubmitError('');
      const res = await fetch(`/api/laws/${law.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body.message || 'Failed to remove law.');
      }

      setLaws((prev) => prev.filter((item) => item.id !== law.id));
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(law.id);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(law.id);
        return next;
      });
      showSuccess('Law removed successfully');
    } catch (removeError) {
      setSubmitError(removeError.message || 'Could not remove law.');
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleExportCSV = () => {
    const headers = ['Law Title', 'Public Law', 'US Citation', 'CFR Citation', 'Effective Date', 'Status'];
    const rows = filtered.map((l) => [
      l.title,
      l.publicLaw,
      l.usCitation,
      l.cfrCitation,
      l.effectiveDate,
      l.status,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laws_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSuccess('File downloaded successfully');
  };

  const handleExportPDF = () => {
    const rows = filtered
      .map(
        (l) => `
        <tr>
          <td>${l.title}</td>
          <td>${l.publicLaw}</td>
          <td>${l.usCitation}</td>
          <td>${l.cfrCitation}</td>
          <td>${l.effectiveDate}</td>
          <td>${l.status}</td>
        </tr>`
      )
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>NuGrant Laws Export</title>
  <style>
    body { font-family: sans-serif; padding: 24px; color: #1e293b; }
    h2 { font-size: 1.2rem; margin-bottom: 16px; }
    p { font-size: 0.85rem; color: #64748b; margin: 0 0 16px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
    th { background: #f8fafc; text-align: left; padding: 8px 12px; font-weight: 600; color: #475569; border-bottom: 2px solid #e2e8f0; }
    td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
  </style>
</head>
<body>
  <h2>NuGrant - Laws &amp; Regulations</h2>
  <p>Exported ${new Date().toLocaleString()}</p>
  <table>
    <thead><tr><th>Law Title</th><th>Public Law</th><th>US Citation</th><th>CFR Citation</th><th>Effective Date</th><th>Status</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=650');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
    showSuccess('File downloaded successfully');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    if (!form.title.trim()) errors.title = 'Law Title is required.';
    return errors;
  };

  const openCreateForm = () => {
    setEditingLawId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSubmitError('');
    setShowForm(true);
  };

  const openEditForm = (law) => {
    setEditingLawId(law.id);
    setForm({
      title: law.title || '',
      publicLaw: law.publicLaw || '',
      effectiveDate: law.effectiveDate || '',
      usCitation: law.usCitation || '',
      cfrCitation: law.cfrCitation || '',
      sourceReference: law.sourceReference || '',
      summary: law.summary || '',
    });
    setFormErrors({});
    setSubmitError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      const res = await fetch(editingLawId ? `/api/laws/${editingLawId}` : '/api/laws', {
        method: editingLawId ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        setSubmitError(data.message || `Failed to ${editingLawId ? 'update' : 'create'} law.`);
        return;
      }
      const savedLaw = await res.json();
      setLaws((prev) => (
        editingLawId
          ? prev.map((law) => (law.id === editingLawId ? savedLaw : law))
          : [...prev, savedLaw]
      ));
      setForm(EMPTY_FORM);
      setEditingLawId(null);
      setFormErrors({});
      setShowForm(false);
      showSuccess(`Law ${editingLawId ? 'updated' : 'created'} successfully`);
    } catch {
      setSubmitError('Could not connect to server.');
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingLawId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setSubmitError('');
  };

  return (
    <div className="laws-page page-stack">

      {successMessage && (
        <div className="programs-success-banner" role="status">
          {successMessage}
        </div>
      )}

      <section className="dashboard-section">
        <div className="section-top">
          <h2>Laws &amp; Policy</h2>
        </div>

        <div className="laws-search-row">
          <div className="laws-search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by law title..."
              value={searchTerm}
              onChange={handleSearch}
              aria-label="Search laws"
            />
          </div>
          {canManage && (
            <div className="programs-actions">
              <button className="programs-export-btn" onClick={handleExportCSV}>
                Export Excel (CSV)
              </button>
              <button className="programs-export-btn" onClick={handleExportPDF}>
                Export PDF
              </button>
              <button className="programs-add-btn" onClick={openCreateForm}>
                + Add Law
              </button>
            </div>
          )}
        </div>

        {fetchError && <p className="section-error">{fetchError}</p>}

        {!fetchError && (
          <>
            <div className="grants-table-wrapper">
              <table className="grants-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        className="laws-row-checkbox"
                        checked={allPageSelected}
                        onChange={handleSelectAll}
                        aria-label="Select all on this page"
                      />
                    </th>
                    <th style={{ width: '44px' }}></th>
                    <th>Law Title</th>
                    <th>Public Law</th>
                    <th>US Citation</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="no-results">
                        No laws match your search.
                      </td>
                    </tr>
                  ) : (
                    paged.map((law) => (
                      <React.Fragment key={law.id}>
                        <tr>
                          <td>
                            <input
                              type="checkbox"
                              className="laws-row-checkbox"
                              checked={selectedIds.has(law.id)}
                              onChange={() => handleToggleSelect(law.id)}
                              aria-label={`Select ${law.title}`}
                            />
                          </td>
                          <td>
                            <button
                              className={`laws-expand-btn${expandedIds.has(law.id) ? ' laws-expand-btn--open' : ''}`}
                              onClick={() => handleToggleExpand(law.id)}
                              aria-label={expandedIds.has(law.id) ? `Collapse ${law.title}` : `Expand ${law.title}`}
                            >
                              {expandedIds.has(law.id) ? '−' : '+'}
                            </button>
                          </td>
                          <td>{law.title}</td>
                          <td>{law.publicLaw}</td>
                          <td>{law.usCitation}</td>
                        </tr>
                        {expandedIds.has(law.id) && (
                          <tr className="laws-expanded-row">
                            <td colSpan="5">
                              <div className="laws-expanded-detail">
                                <span>
                                  <strong>CFR Citation:</strong>{' '}
                                  {law.cfrCitation || '—'}
                                </span>
                                <span>
                                  <strong>Effective Date:</strong>{' '}
                                  {law.effectiveDate || '—'}
                                </span>
                                {canManage && (
                                  <div className="laws-expanded-actions">
                                    <button
                                      type="button"
                                      className="programs-edit-icon-btn"
                                      title="Edit"
                                      aria-label={`Edit ${law.title}`}
                                      onClick={() => openEditForm(law)}
                                    >
                                      <PencilIcon />
                                    </button>
                                    <button
                                      type="button"
                                      className="laws-remove-btn laws-inline-remove-btn"
                                      onClick={() => handleDeleteLaw(law)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="laws-bottom-bar">
              <span className="laws-pagination-info">
                {filtered.length === 0
                  ? 'No entries'
                  : `Showing ${firstIdx + 1} to ${lastIdx} of ${filtered.length} entries`}
              </span>
              <div className="laws-bottom-right">
                {canManage && selectedIds.size > 0 && (
                  <button className="laws-remove-btn" onClick={handleRemoveSelected}>
                    Remove Selected ({selectedIds.size})
                  </button>
                )}
                <div className="laws-pagination-btns">
                  <button
                    className="laws-pagination-btn"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={safePage <= 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="laws-pagination-btn"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={safePage >= totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {showForm && (
        <div
          className="programs-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={editingLawId ? 'Edit Law' : 'Create Law'}
        >
          <div className="programs-modal">
            <h2 className="programs-modal-title">{editingLawId ? 'Edit Law' : 'Create Law'}</h2>
            <form onSubmit={handleSubmit} noValidate>
              <div className="programs-form-grid">

                <div className="programs-form-field programs-form-field--full">
                  <label className="filter-label" htmlFor="law-title">
                    Law Title <span className="required-mark">*</span>
                  </label>
                  <input
                    id="law-title"
                    name="title"
                    type="text"
                    className={`search-input${formErrors.title ? ' input-error' : ''}`}
                    value={form.title}
                    onChange={handleFormChange}
                    placeholder="e.g. Workforce Innovation and Opportunity Act"
                  />
                  {formErrors.title && (
                    <p className="field-error">{formErrors.title}</p>
                  )}
                </div>

                <div className="programs-form-field">
                  <label className="filter-label" htmlFor="law-publicLaw">
                    Public Law
                  </label>
                  <input
                    id="law-publicLaw"
                    name="publicLaw"
                    type="text"
                    className="search-input"
                    value={form.publicLaw}
                    onChange={handleFormChange}
                    placeholder="e.g. Public Law 113-128"
                  />
                </div>

                <div className="programs-form-field">
                  <label className="filter-label" htmlFor="law-effectiveDate">
                    Effective Date
                  </label>
                  <input
                    id="law-effectiveDate"
                    name="effectiveDate"
                    type="date"
                    className="search-input"
                    value={form.effectiveDate}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="programs-form-field">
                  <label className="filter-label" htmlFor="law-usCitation">
                    US Citation
                  </label>
                  <input
                    id="law-usCitation"
                    name="usCitation"
                    type="text"
                    className="search-input"
                    value={form.usCitation}
                    onChange={handleFormChange}
                    placeholder="e.g. 29 U.S.C. § 3101 et seq."
                  />
                </div>

                <div className="programs-form-field">
                  <label className="filter-label" htmlFor="law-cfrCitation">
                    CFR Citation
                  </label>
                  <input
                    id="law-cfrCitation"
                    name="cfrCitation"
                    type="text"
                    className="search-input"
                    value={form.cfrCitation}
                    onChange={handleFormChange}
                    placeholder="e.g. 20 CFR Parts 675-688"
                  />
                </div>

                <div className="programs-form-field programs-form-field--full">
                  <label className="filter-label" htmlFor="law-sourceReference">
                    Source Reference
                  </label>
                  <input
                    id="law-sourceReference"
                    name="sourceReference"
                    type="text"
                    className="search-input"
                    value={form.sourceReference}
                    onChange={handleFormChange}
                    placeholder="Reference link or document name"
                  />
                </div>

                <div className="programs-form-field programs-form-field--full">
                  <label className="filter-label" htmlFor="law-summary">
                    Summary
                  </label>
                  <textarea
                    id="law-summary"
                    name="summary"
                    className="search-input programs-textarea"
                    value={form.summary}
                    onChange={handleFormChange}
                    placeholder="Brief description of the law"
                    rows={3}
                  />
                </div>

              </div>

              {submitError && (
                <p className="field-error programs-submit-error">{submitError}</p>
              )}

              <div className="programs-modal-actions">
                <button type="button" className="reset-filters-btn" onClick={handleCancelForm}>
                  Cancel
                </button>
                <button type="submit" className="programs-add-btn">
                  {editingLawId ? 'Save Changes' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LawsPage;
